<script setup lang="ts">
const lastUpdate = '10/02/2026'

useSeoMeta({
  title: 'Termini e Condizioni',
  description: 'Termini e condizioni di utilizzo del sito e dei servizi'
})
</script>

<template>
  <div class="min-h-screen bg-white text-slate-900 dark:bg-slate-950 dark:text-slate-100">
    <!-- Top bar (stile landing) -->
    <LandingNav />

    <!-- Hero semplice coerente -->
    <section class="border-b border-slate-200 bg-slate-50 dark:border-slate-800 dark:bg-slate-950">
      <UContainer class="py-14 md:py-20">
        <div class="max-w-3xl">
          <UBadge
            class="mb-4 border border-slate-200 bg-white/80 text-slate-700 dark:border-slate-800 dark:bg-slate-950/70 dark:text-slate-200"
          >
            Documentazione legale
          </UBadge>

          <h1 class="text-4xl font-bold tracking-tight text-slate-900 dark:text-white md:text-5xl">
            Termini e Condizioni
          </h1>

          <p class="mt-4 text-lg text-slate-700 dark:text-slate-300">
            Regole di utilizzo del sito e condizioni applicabili ai servizi.
          </p>
        </div>
      </UContainer>
    </section>

    <!-- Contenuto in card (stile landing CTA) -->
    <section>
      <UContainer class="py-10 md:py-14">
        <div class="rounded-3xl border border-slate-200 bg-white p-8 dark:border-slate-800 dark:bg-slate-950 md:p-12">
          <div class="prose prose-slate dark:prose-invert max-w-none">
            <div class="hero">
              <div class="hero-label">
                Documentazione legale
              </div>
              <h1>Termini e Condizioni</h1>
              <div class="hero-meta">
                Ultimo aggiornamento: 14 marzo 2026
              </div>
            </div>

            <hr class="divider">

            <main class="content">
              <!-- 1 -->
              <div class="section">
                <div class="section-number">
                  01
                </div>
                <h2>Informazioni generali</h2>
                <p>
                  Il presente documento disciplina l'utilizzo del sito <strong>www.purejoule.ch</strong> e l'acquisto
                  dei servizi offerti da <strong>Luca Margonar</strong> (persona fisica), con sede in Via del Tiglio 19,
                  6900 Lugano, Svizzera ("Fornitore").
                </p>
                <div class="highlight-box">
                  <p>Contatto: <strong>purejoulech@gmail.com</strong></p>
                </div>
                <p>
                  Accedendo al sito o acquistando i Servizi, l'utente accetta integralmente i presenti Termini e
                  Condizioni.
                </p>
              </div>

              <!-- 2 -->
              <div class="section">
                <div class="section-number">
                  02
                </div>
                <h2>Oggetto dei servizi</h2>
                <p>
                  I servizi offerti comprendono coaching endurance personalizzato, piani di allenamento strutturati,
                  analisi degli allenamenti, supporto tramite piattaforma TrainingPeaks e comunicazione diretta secondo
                  il pacchetto scelto ("Servizi"). I dettagli di ciascun pacchetto sono indicati nella pagina dedicata
                  del sito.
                </p>
              </div>

              <!-- 3 -->
              <div class="section">
                <div class="section-number">
                  03
                </div>
                <h2>Prezzi e pagamenti</h2>
                <p>
                  I prezzi dei Servizi sono indicati in <strong>Franchi Svizzeri (CHF)</strong>. Il Fornitore non è
                  assoggettato all'IVA svizzera.
                </p>
                <p>
                  Le modalità di pagamento sono indicate al momento dell'iscrizione. Il pagamento è dovuto su base
                  <strong>mensile anticipata</strong>, a partire dalla data di attivazione del servizio.
                </p>
              </div>

              <!-- 4 -->
              <div class="section">
                <div class="section-number">
                  04
                </div>
                <h2>Attivazione del servizio</h2>
                <p>Il servizio si attiva a seguito di:</p>
                <div class="steps">
                  <div class="step">
                    <div class="step-num">
                      1
                    </div>
                    <div class="step-text">
                      Invio del modulo di iscrizione da parte dell'utente
                    </div>
                  </div>
                  <div class="step">
                    <div class="step-num">
                      2
                    </div>
                    <div class="step-text">
                      Verifica e <strong>conferma via email</strong> da parte del Fornitore
                    </div>
                  </div>
                  <div class="step">
                    <div class="step-num">
                      3
                    </div>
                    <div class="step-text">
                      Completamento del <strong>pagamento del primo mese</strong>
                    </div>
                  </div>
                </div>
                <p>
                  Il Fornitore si riserva il diritto di rifiutare o non confermare una richiesta di iscrizione, in
                  particolare qualora il pacchetto selezionato non risulti coerente con gli obiettivi dichiarati
                  dall'utente.
                </p>
              </div>

              <!-- 5 -->
              <div class="section">
                <div class="section-number">
                  05
                </div>
                <h2>Durata, rinnovo e disdetta</h2>
                <p>
                  Il servizio è a <strong>rinnovo automatico mensile</strong>. Ogni mese viene fatturato in anticipo.
                </p>
                <div class="highlight-box">
                  <p>
                    L'utente può disdire in qualsiasi momento, <strong>senza preavviso</strong>. La disdetta è efficace
                    a fine del mese in corso: il servizio rimane attivo e utilizzabile fino alla scadenza del periodo
                    già pagato.
                  </p>
                  <p style="margin-top:0.5rem;">
                    Per disdire è sufficiente inviare una comunicazione scritta a <strong>purejoulech@gmail.com</strong>.
                  </p>
                </div>
                <p>
                  Anche il Fornitore può recedere dal contratto in qualsiasi momento, con comunicazione via email e
                  rimborso proporzionale dei giorni di servizio non ancora erogati nel mese in corso.
                </p>
              </div>

              <!-- 6 -->
              <div class="section">
                <div class="section-number">
                  06
                </div>
                <h2>Politica di rimborso</h2>
                <p>
                  Non sono previsti rimborsi parziali per i giorni non fruiti nel mese in corso in caso di disdetta da
                  parte dell'utente. Il servizio rimane comunque accessibile fino a fine mese.
                </p>
                <p>
                  In caso di malfunzionamenti tecnici imputabili al Fornitore che impediscano l'erogazione del servizio
                  per un periodo prolungato, il Fornitore si impegna a concordare una soluzione equa (es. proroga del
                  periodo, rimborso proporzionale).
                </p>
              </div>

              <!-- 7 -->
              <div class="section">
                <div class="section-number">
                  07
                </div>
                <h2>Obblighi dell'utente</h2>
                <p>L'utente si impegna a:</p>
                <ul>
                  <li>
                    Fornire informazioni veritiere e aggiornate al momento dell'iscrizione e durante l'erogazione del
                    servizio
                  </li>
                  <li>
                    Comunicare tempestivamente eventuali variazioni rilevanti (infortuni, cambiamenti di
                    disponibilità, ecc.)
                  </li>
                  <li>
                    Utilizzare i materiali e i piani di allenamento ricevuti esclusivamente per uso personale e non
                    commerciale
                  </li>
                </ul>
              </div>

              <!-- 8 -->
              <div class="section">
                <div class="section-number">
                  08
                </div>
                <h2>Proprietà intellettuale</h2>
                <p>
                  Tutti i contenuti del sito (testi, immagini, piani di allenamento, materiali formativi) sono di
                  proprietà di Luca Margonar o dei rispettivi titolari. È vietata la riproduzione, distribuzione o
                  utilizzo commerciale senza autorizzazione scritta.
                </p>
              </div>

              <!-- 9 -->
              <div class="section">
                <div class="section-number">
                  09
                </div>
                <h2>Disclaimer salute e attività fisica</h2>
                <div class="warning-box">
                  <p>
                    <strong>Importante:</strong> I Servizi offerti hanno finalità di coaching sportivo amatoriale e non
                    costituiscono in alcun modo consulenza medica, diagnosi o terapia. Prima di iniziare qualsiasi
                    programma di allenamento, l'utente è tenuto a verificare il proprio stato di salute con un medico,
                    in particolare in presenza di patologie pregresse o fattori di rischio.
                  </p>
                </div>
                <p>
                  Il Fornitore non è responsabile per infortuni, problemi di salute o danni derivanti dall'esecuzione
                  degli allenamenti suggeriti.
                </p>
              </div>

              <!-- 10 -->
              <div class="section">
                <div class="section-number">
                  10
                </div>
                <h2>Limitazione di responsabilità</h2>
                <p>
                  Nei limiti consentiti dalla legge applicabile, il Fornitore non è responsabile per danni indiretti,
                  consequenziali o perdita di profitto derivanti dall'utilizzo o dalla mancata disponibilità dei
                  Servizi, né per disservizi dovuti a cause di forza maggiore o a terzi (es. interruzioni della
                  piattaforma TrainingPeaks).
                </p>
              </div>

              <!-- 11 -->
              <div class="section">
                <div class="section-number">
                  11
                </div>
                <h2>Modifiche ai Termini</h2>
                <p>
                  Il Fornitore si riserva il diritto di aggiornare i presenti Termini. Le modifiche saranno comunicate
                  via email agli utenti attivi con almeno <strong>14 giorni di anticipo</strong> rispetto alla loro
                  entrata in vigore. L'utilizzo continuato del Servizio dopo tale data costituisce accettazione delle
                  modifiche.
                </p>
              </div>

              <!-- 12 -->
              <div class="section">
                <div class="section-number">
                  12
                </div>
                <h2>Legge applicabile e foro competente</h2>
                <p>
                  I presenti Termini sono disciplinati dal <strong>diritto svizzero</strong>. Per qualsiasi
                  controversia è competente il <strong>foro di Lugano (CH)</strong>, salvo disposizioni inderogabili di
                  legge a favore del consumatore — in particolare per utenti residenti nell'UE, si applicano le norme di
                  tutela del consumatore del paese di residenza.
                </p>
              </div>

              <!-- 13 -->
              <div class="section">
                <div class="section-number">
                  13
                </div>
                <h2>Contatti</h2>
                <p>Per qualsiasi richiesta relativa ai presenti Termini: <strong>purejoulech@gmail.com</strong></p>
              </div>
            </main>
          </div>
        </div>
      </UContainer>
    </section>

    <LandingFooter />
  </div>
</template>

<style>
/* HERO */
.hero {
  padding: 5rem 2rem 3rem;
  max-width: 760px;
  margin: 0 auto;
}

.hero-label {
  font-size: 0.72rem;
  font-weight: 600;
  letter-spacing: 0.15em;
  text-transform: uppercase;
  color: var(--accent);
  margin-bottom: 1.2rem;
}

.hero h1 {
  font-family: 'Syne', sans-serif;
  font-size: clamp(2rem, 5vw, 3rem);
  font-weight: 800;
  letter-spacing: -0.03em;
  line-height: 1.1;
  margin-bottom: 1rem;
}

.hero-meta {
  font-size: 0.85rem;
  color: var(--text-muted);
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.hero-meta::before {
  content: '';
  display: inline-block;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: var(--accent);
}

/* DIVIDER */
.divider {
  max-width: 760px;
  margin: 0 auto 3rem;
  padding: 0 2rem;
  border: none;
  border-top: 1px solid var(--border);
}

/* CONTENT */
.content {
  max-width: 760px;
  margin: 0 auto;
  padding: 0 2rem 6rem;
}

.section {
  margin-bottom: 3.5rem;
  opacity: 0;
  transform: translateY(16px);
  animation: fadeUp 0.5s forwards;
}

.section:nth-child(1) {
  animation-delay: 0.05s;
}

.section:nth-child(2) {
  animation-delay: 0.10s;
}

.section:nth-child(3) {
  animation-delay: 0.15s;
}

.section:nth-child(4) {
  animation-delay: 0.20s;
}

.section:nth-child(5) {
  animation-delay: 0.25s;
}

.section:nth-child(6) {
  animation-delay: 0.30s;
}

.section:nth-child(7) {
  animation-delay: 0.35s;
}

.section:nth-child(8) {
  animation-delay: 0.40s;
}

.section:nth-child(9) {
  animation-delay: 0.45s;
}

.section:nth-child(10) {
  animation-delay: 0.50s;
}

.section:nth-child(11) {
  animation-delay: 0.55s;
}

.section:nth-child(12) {
  animation-delay: 0.60s;
}

.section:nth-child(13) {
  animation-delay: 0.65s;
}

@keyframes fadeUp {
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.section-number {
  font-family: 'Syne', sans-serif;
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--accent);
  margin-bottom: 0.5rem;
}

.section h2 {
  font-family: 'Syne', sans-serif;
  font-size: 1.15rem;
  font-weight: 700;
  letter-spacing: -0.02em;
  margin-bottom: 1rem;
  color: var(--text);
}

.section p {
  color: var(--text-secondary);
  margin-bottom: 0.8rem;
  font-weight: 300;
}

.section p strong {
  color: var(--text);
  font-weight: 500;
}

.section ul {
  list-style: none;
  padding: 0;
  margin: 0.5rem 0 0.8rem;
}

.section ul li {
  color: var(--text-secondary);
  font-weight: 300;
  padding: 0.35rem 0 0.35rem 1.2rem;
  position: relative;
  border-bottom: 1px solid var(--border);
}

.section ul li:last-child {
  border-bottom: none;
}

.section ul li::before {
  content: '—';
  position: absolute;
  left: 0;
  color: var(--accent);
  font-size: 0.75rem;
}

/* HIGHLIGHT BOX */
.highlight-box {
  background: var(--accent-dim);
  border-left: 3px solid var(--accent);
  border-radius: 0 6px 6px 0;
  padding: 1rem 1.2rem;
  margin: 1rem 0;
}

.highlight-box p {
  color: var(--text-secondary);
  font-weight: 300;
  margin: 0 0 0.3rem;
}

.highlight-box p:last-child {
  margin: 0;
}

.highlight-box strong {
  color: var(--text);
  font-weight: 500;
}

/* WARNING BOX */
.warning-box {
  background: rgba(255, 180, 50, 0.08);
  border-left: 3px solid #ffb432;
  border-radius: 0 6px 6px 0;
  padding: 1rem 1.2rem;
  margin: 1rem 0;
}

.warning-box p {
  color: var(--text-secondary);
  font-weight: 300;
  margin: 0;
  font-size: 0.875rem;
}

.warning-box strong {
  color: #ffb432;
  font-weight: 500;
}

/* STEPS */
.steps {
  margin: 1rem 0;
  display: flex;
  flex-direction: column;
  gap: 0;
}

.step {
  display: flex;
  gap: 1rem;
  align-items: flex-start;
  padding: 0.9rem 0;
  border-bottom: 1px solid var(--border);
}

.step:last-child {
  border-bottom: none;
}

.step-num {
  font-family: 'Syne', sans-serif;
  font-size: 0.75rem;
  font-weight: 700;
  color: var(--accent);
  background: var(--accent-dim);
  border-radius: 4px;
  min-width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  margin-top: 0.1rem;
}

.step-text {
  color: var(--text-secondary);
  font-weight: 300;
  font-size: 0.9rem;
  line-height: 1.6;
}

.step-text strong {
  color: var(--text);
  font-weight: 500;
}

/* FOOTER */
footer {
  border-top: 1px solid var(--border);
  padding: 2rem;
  text-align: center;
  font-size: 0.8rem;
  color: var(--text-muted);
}
</style>
